import ErrorMessage from '@/components/ErrorMessage';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { UpsertUser, User, UserGenderList, UserRolesList } from '@/models/user.model';
import { upsertUser } from '@/services/user.service';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { Divider, Form, Stack, SelectPicker, Schema, Checkbox, IconButton, Col, Grid, Row, FormInstance, HStack, TagPicker,} from 'rsuite';
import { useNavigate } from 'react-router-dom';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import FormControl from 'rsuite/esm/FormControl';
import PageContent from '@/components/PageContent';
import ImageUploader from '@/components/ImageUploader';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import { MdPerson } from 'react-icons/md';
import { FeatureRuleNameList } from '@/models/user-setting.model';

const modelSchemaNew = Schema.Model<UpsertUser>({
  id: Schema.Types.NumberType(),
  firstName: Schema.Types.StringType(),
  lastName: Schema.Types.StringType(),
  username: Schema.Types.StringType().isRequired('This field is required.'),
  phoneNumber: Schema.Types.StringType(),
  email: Schema.Types.StringType(),
  role: Schema.Types.NumberType().isRequired('This field is required.'),
  gender: Schema.Types.NumberType(),
  password: Schema.Types.StringType().isRequired('Password is required'),
  confirmPassword: Schema.Types.StringType().isRequired('Required'),
  isPhoneVerified: Schema.Types.BooleanType().isRequired('Required'),
  isEmailVerified: Schema.Types.BooleanType().isRequired('Required'),
  profilePicture:  Schema.Types.StringType(),
});

const modelSchemaEdit = Schema.Model<UpsertUser>({
  id: Schema.Types.NumberType(),
  firstName: Schema.Types.StringType(),
  lastName: Schema.Types.StringType(),
  username: Schema.Types.StringType().isRequired('This field is required.'),
  phoneNumber: Schema.Types.StringType(),
  email: Schema.Types.StringType(),
  role: Schema.Types.NumberType().isRequired('This field is required.'),
  gender: Schema.Types.NumberType(),
  password: Schema.Types.StringType(),
  confirmPassword: Schema.Types.StringType(),
  isPhoneVerified: Schema.Types.BooleanType().isRequired('Required'),
  isEmailVerified: Schema.Types.BooleanType().isRequired('Required'),
  profilePicture:  Schema.Types.StringType(),
});

const emptyModel: UpsertUser = {
  id: undefined,
  firstName: '',
  lastName: '',
  role: 0,
  gender: 0,
  username: '',
  password: '',
  confirmPassword: '',
  phoneNumber: undefined,
  email: undefined,
  profilePicture: '',
  isPhoneVerified: false,
  isEmailVerified: false,
};

interface UserFormProps {
  formObject?: User,
  pageState: PageState,
}

const UserForm: React.FC<UserFormProps> = ({formObject, pageState}) => {
  const [formValue, setFormValue] = useState<UpsertUser>(emptyModel);
  const [formError, setFormError] = useState({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel|undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);
  
  useEffect(() => {
    if(pageState == PageState.New){
      setFormValue({...emptyModel, });
      return;
    }

    if (formObject) {
      const castedModel: UpsertUser = { 
        ...formObject, 
      };
      setFormValue(castedModel);
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject]);

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log(formError);
      return;
    }
    setLoading(true);
    setErrorModel(undefined);
    try {
      const castedModel: UpsertUser = { 
        ...formValue,
        email: formValue.email || undefined
      };
      const result: BaseResponseModel<User> = await upsertUser(castedModel);
      if (result.success) {
        nav('/users');
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }
    finally {
      setLoading(false);
    }
  };
  
  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  return (
    <PageContent>
      <Form name='upsert-user' fluid 
        layout='vertical'
        ref={formRef}
        disabled = {loading}
        onChange={handleFormChange}
        onCheck={setFormError}
        formValue={formValue}
        model={pageState == PageState.Edit ? modelSchemaEdit : modelSchemaNew}
      >
      <Grid w='100%'>
          <Row >
            <Col span={{xs: 24, lg:6, xl:4}} style={{textAlign: 'center'}}>
              <Form.Group>
                <Form.Label>Profile Picture</Form.Label>
                <Form.Control
                  accepter={ImageUploader}
                  name="profilePicture"
                  defaultIcon={MdPerson}
                  height="150px"
                  width="150px"
                  url={formValue.profilePicture}
                  objectStorageCategory={ObjectStorageFileCategory.User}
                  onChange={url => setFormValue(pre => ({...pre, profilePicture: url}))}                  
                />
              </Form.Group>
            </Col> 
            <Col span={{xs: 24, lg:18, xl:20}} >
              <Row>
                <Col span={{xs: 24, xl:8}} >
                  <Form.Group>
                    <Form.Label>First Name</Form.Label>
                    <Form.Control name="firstName" value={formValue.firstName ?? ''}/>
                  </Form.Group>
                </Col>
                <Col span={{xs: 24, xl:8}} >
                  <Form.Group>
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control name="lastName" value={formValue.lastName ?? ''}/>
                  </Form.Group>
                </Col>
                <Col span={{xs: 24, xl:8}} >
                  <Form.Group>
                    <Form.Label>Username</Form.Label>
                    <Form.Control name="username" value={formValue.username ?? ''}/>
                  </Form.Group>              
                </Col>
              </Row>
              <Row>
                <Col span={{xs: 24, xl:8}} >
                  <Form.Group>
                    <Form.Label>Phone Number</Form.Label>
                    <Form.Control name="phoneNumber" value={formValue.phoneNumber ?? ''}/>
                  </Form.Group>              
                </Col>                
                <Col span={{xs: 24, xl:8}} >
                  <Form.Group>
                    <Form.Label>Email</Form.Label>
                    <Form.Control name="email" value={formValue.email ?? ''}/>
                  </Form.Group>
                </Col>                
                <Col span={{xs: 24, xl:4}} >
                  <Form.Label>Is Phone Verified</Form.Label>
                  <FormControl name="isPhoneVerified" accepter={Checkbox} 
                    checked={!!formValue.isPhoneVerified} 
                    onChange={(val, chk) => setFormValue(pre => ({ ...pre, isPhoneVerified: chk })) } 
                    />
                </Col>
                <Col span={{xs: 24, xl:4}} >
                  <Form.Label>Is Email Verified</Form.Label>
                  <FormControl name="isEmailVerified" accepter={Checkbox}
                    checked={!!formValue.isEmailVerified} 
                    onChange={(val, chk) => setFormValue(pre => ({ ...pre, isEmailVerified: chk })) } 
                  />                
                </Col>                 
              </Row>
              <Row >
                <Col span={{xs: 24, xl:4}} >
                  <Form.Group>
                    <Form.Label>Role</Form.Label>
                    <Form.Control name="role" accepter={SelectPicker} data={UserRolesList} block/>
                  </Form.Group>              
                </Col>
                <Col span={{xs: 24, xl:4}} >
                  <Form.Group>
                    <Form.Label>Gender</Form.Label>
                    <Form.Control name="gender" accepter={SelectPicker} data={UserGenderList} block/>
                  </Form.Group>              
                </Col>
                <Col span={{xs: 24, xl:8}} >
                  <Form.Group>
                    <Form.Label>Password</Form.Label>
                    <Form.Control name="password" type="password" value={formValue.password ?? ''}/>
                    <Form.Text>Leave password fields blank, if you don&apos;t want to change password</Form.Text>
                  </Form.Group>              
                </Col>
                <Col span={{xs: 24, xl:8}} >
                  <Form.Group>
                    <Form.Label>Confirm Password</Form.Label>
                    <Form.Control name="confirmPassword" type="password" value={formValue.confirmPassword ?? ''}/>
                  </Form.Group>              
                </Col>
              </Row>
            </Col>
          </Row>

        </Grid>
      </Form>

      <Divider />

      {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}

      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>      
    </PageContent>

  );
};

export default UserForm;
